import { useI18n } from '@ecom/i18n';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { MerchantOrder, MerchantOrderPage } from '../types';

import { formatCatalogPrice } from '@/features/products/format-price';
import { apiRequest } from '@/lib/api-client';
import { pushFlagOn } from '@/lib/push';
import { useSession } from '@/lib/session';

/**
 * « Nouvelle commande » — tells the merchant, wherever they are in the dashboard, that a customer just ordered on the
 * shop: a card in the corner, a short chime, the count in the tab title while the tab is in the background, and a
 * notification of the computer once the merchant allowed them (`PushBell`).
 *
 * No new server route: this watches the newest online orders (`GET /merchant/orders?source=online`). Its query key
 * sits under `['orders', shop]`, which the live stream (migration 0098) refetches on every order change — and every
 * 30 s while the stream is down — so an order is announced within a second, and one placed while the dashboard was
 * offline is announced on reconnection. The orders already there when the dashboard opens are never announced;
 * orders the merchant enters themselves (manual) are not watched.
 */

const WATCH_LIMIT = 10;
const CARD_MS = 12_000;
const MAX_CARDS = 3;

export function useNewestOnlineOrders(): MerchantOrder[] | undefined {
  const { accessToken, activeShopId } = useSession();
  const query = useQuery({
    queryKey: ['orders', activeShopId, 'newest-online'],
    queryFn: async () =>
      apiRequest<MerchantOrderPage>(
        `/merchant/orders?limit=${String(WATCH_LIMIT)}&offset=0&source=online`,
        { accessToken, shopId: activeShopId },
      ),
    enabled: accessToken !== null && activeShopId !== null,
  });
  return query.data?.orders.slice();
}

/**
 * The orders of `orders` not in `seen`, oldest first, and the updated `seen`. The first call (`seen` null) only
 * records what is already there.
 */
export function detectNewOrders(
  seen: ReadonlySet<string> | null,
  orders: readonly MerchantOrder[],
): { readonly fresh: MerchantOrder[]; readonly seen: Set<string> } {
  const next = new Set(seen ?? []);
  const fresh: MerchantOrder[] = [];
  for (const order of orders) {
    if (seen !== null && !seen.has(order.id)) fresh.push(order);
    next.add(order.id);
  }
  return { fresh: fresh.reverse(), seen: next };
}

/** Two soft notes (no audio file to ship). Silently skipped where audio is unavailable or not yet allowed. */
function chime(): void {
  try {
    // Absent in some test environments and old browsers, whatever the DOM typings say.
    const AudioCtx = (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext;
    if (AudioCtx === undefined) return;
    const context = new AudioCtx();
    [880, 1320].forEach((frequency, index) => {
      const start = context.currentTime + index * 0.16;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.4);
    });
    setTimeout(() => void context.close(), 1_000);
  } catch {
    // No sound is fine: the card and the title still announce the order.
  }
}

function desktopNotificationsAvailable(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function NewOrderAlerts(): ReactElement | null {
  const { t } = useI18n();
  const orders = useNewestOnlineOrders();
  const seenRef = useRef<Set<string> | null>(null);
  const [cards, setCards] = useState<readonly MerchantOrder[]>([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (orders === undefined) return;
    const { fresh, seen } = detectNewOrders(seenRef.current, orders);
    seenRef.current = seen;
    if (fresh.length === 0) return;

    setCards((current) => [...fresh.slice().reverse(), ...current].slice(0, MAX_CARDS));
    chime();
    if (document.visibilityState === 'hidden') setUnread((count) => count + fresh.length);
    // With phone notifications on (`PushBell`), the service worker shows the system notification itself.
    if (desktopNotificationsAvailable() && Notification.permission === 'granted' && !pushFlagOn()) {
      for (const order of fresh) {
        const notification = new Notification(
          t('merchant.newOrder.title', { number: order.orderNumber }),
          {
            body: `${order.customerName} · ${formatCatalogPrice(order.grandTotal)}`,
            tag: `order-${order.id}`,
          },
        );
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    }
  }, [orders, t]);

  // Each card leaves by itself after a while.
  useEffect(() => {
    if (cards.length === 0) return undefined;
    const timer = setTimeout(() => {
      setCards((current) => current.slice(0, -1));
    }, CARD_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [cards]);

  // « (2) Souk » in the tab while orders arrived unseen; cleared when the merchant comes back to the tab.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s/u, '');
    document.title = unread > 0 ? `(${String(unread)}) ${base}` : base;
    if (unread === 0) return undefined;
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') setUnread(0);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [unread]);

  if (cards.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed end-4 top-20 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
      aria-live="polite"
    >
      {cards.map((order) => (
        <div
          key={order.id}
          role="status"
          className="souk-alert pointer-events-auto flex items-start gap-3 rounded-2xl border border-line bg-surface p-4 shadow-popover"
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accentSoft text-accentInk"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6" />
              <circle cx="10" cy="20" r="1" />
              <circle cx="17" cy="20" r="1" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink">
              {t('merchant.newOrder.title', { number: order.orderNumber })}
            </p>
            <p className="truncate text-sm text-inkSoft">
              {order.customerName} ·{' '}
              <span className="num">{formatCatalogPrice(order.grandTotal)}</span>
            </p>
            <Link
              to="/orders"
              className="mt-1 inline-block text-sm font-semibold text-accent hover:underline"
              onClick={() => {
                setCards((current) => current.filter((card) => card.id !== order.id));
              }}
            >
              {t('merchant.newOrder.view')}
            </Link>
          </div>
          <button
            type="button"
            aria-label={t('merchant.newOrder.dismiss')}
            className="rounded-md p-1 text-inkFaint hover:bg-surfaceHover hover:text-ink"
            onClick={() => {
              setCards((current) => current.filter((card) => card.id !== order.id));
            }}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
